import React, { memo, useState, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '../shared/Icons';
import { useNavigation } from '../../contexts/NavigationProvider';
import Logo from './shared/Logo';
import UserMenu from './shared/UserMenu';
import UserAvatar from './shared/UserAvatar';
import NavigationIcons from './shared/NavigationIcons';
import NavigationMenu from './shared/NavigationMenu';
import { KeyboardArrowDown, TableRows, ViewSidebar } from '@mui/icons-material';
import { useTracking } from '../../hooks/useTracking';

const TopNavigation: React.FC = memo(() => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isSideNav, setIsSideNav, mobileOpen, setMobileOpen, navigationRoutes } = useNavigation();
  const desktopTrack = useTracking({
    widgetType: 'top_navigation_menu',
    enableTracking: true,
    globalTrackingEnabled: true
  });

  const mobileTrack = useTracking({
    widgetType: 'mobile_navigation_menu',
    enableTracking: true,
    globalTrackingEnabled: true
  });
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const [docMenuAnchor, setDocMenuAnchor] = useState<HTMLElement | null>(null);
  const [mobileDocMenuAnchor, setMobileDocMenuAnchor] = useState<HTMLElement | null>(null);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(!mobileOpen);
  }, [mobileOpen, setMobileOpen]);

  const handleNavPositionChange = useCallback(() => {
    setIsSideNav(!isSideNav);
    if (mobileOpen) setMobileOpen(false);
  }, [isSideNav, setIsSideNav, mobileOpen, setMobileOpen]);

  const handleUserMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  }, []);

  const handleUserMenuClose = useCallback(() => {
    setUserMenuAnchor(null);
  }, []);

  const handleDocMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setDocMenuAnchor(event.currentTarget);
  }, []);

  const handleDocMenuClose = useCallback(() => {
    setDocMenuAnchor(null);
  }, []);

  const handleMobileDocMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMobileDocMenuAnchor(event.currentTarget);
  }, []);

  const handleMobileDocMenuClose = useCallback(() => {
    setMobileDocMenuAnchor(null);
  }, []);

  const isActive = useCallback(
    (path: string): boolean => {
      const currentPath = location.pathname.split('?')[0].split('#')[0];
      if (path === '/') {
        return currentPath === path;
      }
      return currentPath.startsWith(path) || currentPath === path;
    },
    [location.pathname]
  );

  const renderTopNavItems = useCallback(
    () => (
      <>
        {navigationRoutes.map((item: any) => {
          if (item.subitems) {
            return (
              <div key={item.title}>
                <Button
                  color="inherit"
                  endIcon={<KeyboardArrowDown />}
                  onClick={handleDocMenuOpen}
                  sx={{
                    mx: 0.5,
                    px: 1.5,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    borderRadius: '4px',
                    height: '40px',
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.06)'
                    }
                  }}
                >
                  {item.title}
                </Button>
                <NavigationMenu
                  anchorEl={docMenuAnchor}
                  open={Boolean(docMenuAnchor)}
                  onClose={handleDocMenuClose}
                  items={item.subitems}
                  isActive={isActive}
                />
              </div>
            );
          }

          if (item.path) {
            return (
              <Button
                key={item.title}
                color="inherit"
                component={Link}
                {...(item.isExternal
                  ? {
                      href: item.path,
                      target: '_blank',
                      rel: 'noopener noreferrer'
                    }
                  : {
                      to: item.path
                    })}
                to={item.path}
                onClick={() => {
                  desktopTrack(
                    'navigation_click',
                    'click',
                    'NavigationMenuClick',
                    {
                      navigationPath: item.path,
                      menu: item.title
                    },
                    'raw',
                    '2'
                  );
                }}
                sx={{
                  mx: 0.5,
                  px: 1.5,
                  textTransform: 'none',
                  fontWeight: isActive(item.path) ? 600 : 500,
                  fontSize: '0.9rem',
                  borderRadius: '4px',
                  height: '40px',
                  position: 'relative',
                  backgroundColor: isActive(item.path) ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                  // color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.06)'
                  },
                  '&::after': isActive(item.path)
                    ? {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: '20%',
                        right: '20%',
                        height: '3px',
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: '3px 3px 0 0'
                      }
                    : {}
                }}
              >
                {item.title}
              </Button>
            );
          }

          return null;
        })}
      </>
    ),
    [navigationRoutes, desktopTrack, handleDocMenuOpen, docMenuAnchor, handleDocMenuClose, isActive, theme.palette.primary.main]
  );

  const handleMobileNavClick = useCallback(
    (item: any) => {
      if (item.path && !item.isExternal) {
        mobileTrack(
          'navigation_click',
          'click',
          'NavigationMenuClick',
          {
            navigationPath: item.path,
            menu: item.title
          },
          'raw',
          '2'
        );
      }
      setMobileOpen(false);
    },
    [mobileTrack, setMobileOpen]
  );
  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
          }),
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}
      >
        <Toolbar sx={{ minHeight: '63px' }}>
          {isMobile ? (
            <>
              <IconButton
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  mr: 2,
                  color: theme.palette.primary.main
                }}
              >
                <Box component="svg" sx={{ width: 24, height: 24 }}>
                  <path d="M3,6H21V8H3V6M3,11H21V13H3V11M3,16H21V18H3V16Z" fill="currentColor" />
                </Box>
              </IconButton>
              <Logo variant="mobile" sx={{ flexGrow: 1 }} />
            </>
          ) : (
            <>
              <Logo
                variant="header"
                sx={{
                  flexGrow: 0,
                  mr: 3,
                  fontWeight: 600
                }}
              />
              <Box sx={{ flexGrow: 1, display: 'flex' }}>{renderTopNavItems()}</Box>
            </>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!isMobile && (
              <Tooltip title="Switch to side navigation">
                <IconButton
                  onClick={handleNavPositionChange}
                  sx={{
                    mr: 1,
                    color: theme.palette.primary.main,
                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.06)' }
                  }}
                >
                  <ViewSidebar />
                </IconButton>
              </Tooltip>
            )}

            <NavigationIcons />
            <UserAvatar onClick={handleUserMenuOpen} />
            <UserMenu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={handleUserMenuClose} />
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 250,
            boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <Box
          sx={{
            width: 250,
            height: '100%',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                mr: 2,
                backgroundColor: theme.palette.primary.main
              }}
            >
              <UserAvatar size={40} />
            </Avatar>
          </Box>

          <List
            component="nav"
            sx={{
              p: 1,
              flexGrow: 1,
              overflowY: 'auto'
            }}
          >
            {navigationRoutes.map((item: any) => {
              if (item.subitems) {
                return (
                  <React.Fragment key={item.title}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={handleMobileDocMenuOpen}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ color: theme.palette.primary.main, minWidth: 40 }}>
                          <Icon name={item.iconName} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontSize: 14,
                            fontWeight: 500
                          }}
                        />
                      </ListItemButton>
                    </ListItem>

                    <NavigationMenu
                      anchorEl={mobileDocMenuAnchor}
                      open={Boolean(mobileDocMenuAnchor)}
                      onClose={handleMobileDocMenuClose}
                      items={item.subitems}
                      isActive={isActive}
                    />
                  </React.Fragment>
                );
              }

              if (item.path) {
                return (
                  <ListItem key={item.title} disablePadding>
                    <ListItemButton
                      component={Link}
                      {...(item.isExternal
                        ? {
                            href: item.path,
                            target: '_blank',
                            rel: 'noopener noreferrer'
                          }
                        : {
                            to: item.path
                          })}
                      to={item.path}
                      selected={isActive(item.path)}
                      onClick={() => handleMobileNavClick(item)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        position: 'relative',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)'
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(25, 118, 210, 0.12)',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 4,
                            backgroundColor: theme.palette.primary.main,
                            borderRadius: '4px 0 0 4px'
                          }
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.16)'
                        }
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: isActive(item.path) ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.6)',
                          minWidth: 40
                        }}
                      >
                        <Icon name={item.iconName} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: isActive(item.path) ? 600 : 500,
                          color: isActive(item.path) ? theme.palette.primary.main : 'inherit'
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              }

              return null;
            })}
          </List>
        </Box>
      </Drawer>
    </>
  );
});

TopNavigation.displayName = 'TopNavigation';

export default TopNavigation;
